/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FunctionComponent, useEffect, useState } from "react";
import { BEM } from "@adapptio/lib-components";
import { FC_ANCHOR, FC_DIRECTION, FC_POSITION, FC_SIZE, FloatingContainer } from "../FloatingContainer";
import { FadeInOut } from "../FadeInOut";

const OPEN_DELAY_DEFAULT = 400;
const SHORTEN_DELAY_TIMEOUT = 500;
const SHORTEN_DELAY_OPEN_DELAY = 200;

let tooltipWasActive = false;
let tooltipWasActiveTimeout = null;

// Helpers to check if any tooltip was showed recently. See `preventShortenDelay` prop.
function setTooltipWasActive() {
	if (tooltipWasActiveTimeout) {
		clearTimeout(tooltipWasActiveTimeout);
	}

	tooltipWasActive = true;
}

function unsetTooltipWasActive() {
	if (tooltipWasActiveTimeout) {
		clearTimeout(tooltipWasActiveTimeout);
	}

	tooltipWasActiveTimeout = setTimeout(() => (tooltipWasActive = false), SHORTEN_DELAY_TIMEOUT);
}

/**
 * Tooltip props
 */
export interface ITooltipProps {
	/** Element to display tooltip for */
	for: React.MutableRefObject<HTMLElement>;
	/** Where to place tooltip relative to target element */
	anchor?: FC_ANCHOR;
	/** Custom class name */
	className?: string;
	/** Delay in milliseconds before showing the tooltip - defaults to OPEN_DELAY_DEFAULT */
	openDelay?: number;
	/** Prevent showing tooltip faster if other tooltip was showed recently */
	preventShortenDelay?: boolean;
	/** If displaying tooltip is disabled */
	disabled?: boolean;
}

/**
 * Action Button component
 */
export const Tooltip: FunctionComponent<ITooltipProps> = ({
	for: target,
	anchor,
	className,
	openDelay,
	preventShortenDelay,
	children,
	disabled
}) => {
	const [ show, setShow ] = useState(false);

	useEffect(() => {
		// Cache element for better cleanup
		const targetEl = target.current;

		const unsubscribes = [];

		// Prevent tooltip if disabled or without target element
		if (!targetEl || disabled) {
			setShow(false);
			unsetTooltipWasActive();
			return;
		}

		let timeoutId;

		const wipeTimeout = () => {
			if (timeoutId) {
				clearTimeout(timeoutId);
				timeoutId = undefined;
			}
		};

		const handleEnter = () => {
			wipeTimeout();

			if (disabled) {
				return false;
			}

			const _openDelay =
				!preventShortenDelay && tooltipWasActive ? SHORTEN_DELAY_OPEN_DELAY : openDelay;

			timeoutId = setTimeout(() => {
				setShow(true);
				setTooltipWasActive();
				wipeTimeout();
			}, _openDelay);
		};

		const handleLeave = () => {
			wipeTimeout();
			setShow(false);
			unsetTooltipWasActive();
		};

		targetEl.addEventListener("mouseenter", handleEnter);
		targetEl.addEventListener("mouseleave", handleLeave);
		targetEl.addEventListener("focus", handleEnter);
		targetEl.addEventListener("blur", handleLeave);
		targetEl.addEventListener("dragstart", handleLeave);

		unsubscribes.push(() => targetEl.removeEventListener("mouseenter", handleEnter));
		unsubscribes.push(() => targetEl.removeEventListener("mouseleave", handleLeave));
		unsubscribes.push(() => targetEl.removeEventListener("focus", handleEnter));
		unsubscribes.push(() => targetEl.removeEventListener("blur", handleLeave));
		unsubscribes.push(() => targetEl.removeEventListener("dragstart", handleLeave));

		return () => {
			wipeTimeout();

			unsubscribes.forEach((unsubscribe) => unsubscribe());
		};
	}, [ target.current, disabled ]);

	return (
		<FadeInOut
			isVisible={show}
			fadeOutDelay={250}
			render={(isVisible) => {
				const baseClass = BEM("edt-tooltip", null, {
					"is-visible": isVisible
				});

				if (className) {
					baseClass.add(className);
				}

				return (
					<FloatingContainer
						parent={target}
						anchor={anchor || FC_ANCHOR.CENTER_TOP}
						direction={FC_DIRECTION.HR}
						size={FC_SIZE.AUTO}
						position={FC_POSITION.AUTO}
						className={baseClass.toClassName()}
						addArrow={true}
						offset={0}
						safePadding={0}
						updateInterval={200}
					>
						<div className={baseClass.element("content").toClassName()}>{children}</div>
					</FloatingContainer>
				);
			}}
		/>
	);
};

Tooltip.defaultProps = {
	openDelay: OPEN_DELAY_DEFAULT
};
